import { useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "../lib/api";
import { useWorkspace } from "../store/WorkspaceContext";
import { useAuth } from "../store/AuthContext";
import { fmtIn, mirrorPiece, rotatePieceTo, worldPoints, selfIntersects, untangle, offsetPolygon, polysIntersect } from "../engine/geometry";
import { applyNest, nestOpts, PREVIEW_ROT, roundedPoints, setTopExtendValue, setEdgeOffsetValue, addToRoll } from "../engine/layout";
import { partsLayout, addGroupToRoll } from "../engine/pairs";
import { EYEBROW_DEFAULT, eyebrowCut, eyebrowSource, clipX } from "../engine/eyebrow";
import { knifeCut } from "../engine/knife";
import { WRAP_DEFAULT, wrapPiece, mirrorWrapSegs } from "../engine/wrap";
import { bbox } from "../engine/geometry";
import { plotterConnected, cutterDevices, rawQueueFor } from "../engine/detect";
import { DEFAULT_CONNECTION } from "../lib/transport";
import { bundleNow, isOffline, localStatus } from "../lib/offlineStore";

export const useActions = () => {
  const { state, dispatch } = useWorkspace();
  const { user } = useAuth();
  const [saving, setSaving] = useState(false);
  const sel = state.pieces.find((p) => p.id === state.selectedId);
  const locked = state.locked;
  const set = (patch) => dispatch({ type: "SET", patch });
  const upd = (patch) => dispatch({ type: "UPDATE_PIECE", id: sel.id, patch, commit: true });
  const included = state.pieces.filter((p) => p.included);

  // Rotate: a joined PPF / decal set turns as one block about its centre; a single piece (or a split set) turns on its own
  const rotate = (d) => {
    const mates = sel?.setKey && !sel.loose ? state.pieces.filter((p) => p.setKey === sel.setKey && p.pieceType === sel.pieceType && p.included && !p.loose) : [];
    if (mates.length < 2) { const np = rotatePieceTo(sel, sel.rotation + d); return upd({ rotation: np.rotation, x: np.x, y: np.y }); }
    const g = bbox(mates.flatMap((p) => worldPoints(p)));
    const rad = (d * Math.PI) / 180, cos = Math.cos(rad), sin = Math.sin(rad);
    const turned = {};
    mates.forEach((p) => {
      const c = bbox(worldPoints(p)), np = rotatePieceTo(p, p.rotation + d);
      const dx = c.cx - g.cx, dy = c.cy - g.cy;
      turned[p.id] = { rotation: np.rotation, x: np.x + (g.cx + dx * cos - dy * sin) - c.cx, y: np.y + (g.cy + dx * sin + dy * cos) - c.cy };
    });
    dispatch({ type: "SET_PIECES", pieces: state.pieces.map((p) => (turned[p.id] ? { ...p, ...turned[p.id] } : p)), commit: true });
  };
  const mirror = () => { const np = mirrorPiece(sel); upd({ points: np.points, holes: np.holes, x: np.x, y: np.y, dirty: !sel.decalId, edited: true, mirrored: !sel.mirrored }); };
  const nest = () => {
    if (!included.length) return toast.error("Load a vehicle first");
    const { pieces, result } = applyNest(state.pieces, state);
    dispatch({ type: "SET_PIECES", pieces, commit: true, nestResult: result });
    set({ fitNonce: state.fitNonce + 1 });
    if (result.overflow.length) toast.warning(`${result.overflow.length} piece(s) don't fit a ${state.rollWidth}" roll`);
    else toast.success(`Nested · ${fmtIn(result.rollLength)} film · ${(result.utilization * 100).toFixed(0)}% efficiency`);
  };
  const emptyRoll = () => { dispatch({ type: "SET_PIECES", pieces: state.pieces.map((p) => ({ ...p, included: false })), commit: true }); set({ selectedId: null }); };
  // Shadow Cut: the toolbar box switches it on; with it on, a click on a pattern (no drag) picks / unpicks that pattern for a weeding contour
  const shadowPieces = included.filter((p) => p.shadow && !p.multi);
  const toggleShadowPiece = (id) => {
    const p = state.pieces.find((q) => q.id === id);
    if (!p || p.multi) return;
    dispatch({ type: "UPDATE_PIECE", id, patch: { shadow: !p.shadow }, commit: true });
    toast.success(p.shadow ? `Shadow cut removed — ${p.name}` : `Shadow cut · ${p.name} · ${fmtIn(state.shadowGap)} gap`, { duration: 1800 });
  };
  const setShadowGap = (v) => { localStorage.setItem("xpattern.shadowGap.v1", String(v)); set({ shadowGap: v }); };
  // Multi-part sets (eyebrow halves, slider back glass) on the roll: the parts of the selected piece's set, when there are 2+.
  const setMates = sel?.setKey ? state.pieces.filter((p) => p.setKey === sel.setKey && p.pieceType === sel.pieceType) : [];
  const setSplit = setMates.length > 1 && setMates.some((p) => p.loose);
  // Split: the parts stop travelling as one block — each half nests and drags on its own (Auto-nest leaves them apart too).
  const splitSet = () => {
    if (setMates.length < 2) return;
    const ids = new Set(setMates.map((p) => p.id));
    dispatch({ type: "SET_PIECES", pieces: state.pieces.map((p) => (ids.has(p.id) ? { ...p, loose: true, groupId: null } : p)), commit: true });
    toast.success(`${setMates.length} parts split — move or nest them separately`);
  };
  // Back together: re-laid as one row and nested as a block again.
  const joinSet = () => {
    if (setMates.length < 2) return;
    const ids = setMates.map((p) => p.id);
    const base = state.pieces.map((p) => (ids.includes(p.id) ? { ...p, loose: false, included: false, groupId: null } : p));
    const lay = partsLayout(setMates.map((p) => ({ ...p, loose: false })), 0.5 + state.spacing, state.rollWidth);
    const fixed = base.filter((p) => p.included).map((p) => ({ id: p.id, x: p.x, y: p.y, rotation: p.rotation, world: worldPoints(p) }));
    let { pieces, overflow } = addGroupToRoll(base, ids, lay, { ...nestOpts(state), rotations: [PREVIEW_ROT] }, fixed);
    if (overflow.length) ({ pieces, overflow } = addGroupToRoll(base, ids, lay, nestOpts(state), fixed));
    if (overflow.length) return toast.error(`The parts don't fit side by side on a ${state.rollWidth}" roll`);
    const gid = `set-${sel.setKey}`;
    dispatch({ type: "SET_PIECES", pieces: pieces.map((p) => (ids.includes(p.id) ? { ...p, groupId: gid } : p)), commit: true });
    set({ fitNonce: state.fitNonce + 1 });
    toast.success(`${ids.length} parts back together`);
  };
  // Permanently delete a template from the library (Owner: any; customers: own uploads). Confirms first.
  const deletePattern = async (p = sel) => {
    if (!p) return;
    const name = p.name || "this template";
    if (!window.confirm(`Delete "${name}" from the ${state.vehicle ? `${state.vehicle.make} ${state.vehicle.model}` : ""} library for everyone? All versions are removed. This cannot be undone.`)) return;
    try {
      const r = await api.deletePattern(p.patternId);
      dispatch({ type: "REMOVE_PIECE", id: p.id });
      toast.success(`Deleted ${name}${r.patterns_left === 0 ? " — this vehicle has no templates left" : ""}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };
  const removeSelected = () => {
    if (!sel) return;
    // taking a piece off the roll dissolves any combo block it belonged to, so the rest move on their own again
    dispatch({ type: "SET_PIECES", pieces: state.pieces.map((q) => (q.id === sel.id ? { ...q, included: false, groupId: null } : sel.groupId && q.groupId === sel.groupId ? { ...q, groupId: null } : q)), commit: true });
    set({ selectedId: null });
  };
  const saveAll = async () => {
    const dirty = state.pieces.filter((p) => p.dirty && !p.decalId && !p.eyebrow);  // customers' own decals and roll-only eyebrows are job-only — nothing to store
    if (!dirty.length) return toast.info("No edited templates to save");
    setSaving(true);
    try {
      for (const p of dirty) {
        const res = await api.saveVersion(p.patternId, { points: p.points, holes: p.holes || [], notes: "Saved from workspace" });
        dispatch({ type: "UPDATE_PIECE", id: p.id, patch: { basePoints: res.points, version: res.version, dirty: false, appliedOffset: 0 } });
      }
      toast.success(`Saved ${dirty.length} template version(s)`);
    } catch (e) { toast.error(apiError(e, "Save failed"), { duration: 8000 }); } finally { setSaving(false); }
  };
  const cut = async () => {
    if (user && !user.entitled) return set({ dialog: "subscribe" });
    // a hairpin loop left by an offset / rounding / hand edit would fail the cut preflight: cut it away here so the cut goes ahead
    const fixed = state.pieces.filter((p) => p.included && p.points.length >= 3 && selfIntersects(p.points)).map((p) => [p, untangle(p.points)]).filter(([, pts]) => pts);
    for (const [p, pts] of fixed) dispatch({ type: "UPDATE_PIECE", id: p.id, patch: { points: pts, dirty: true, edited: true }, commit: true });
    if (fixed.length) toast.success(`Fixed a tiny loop in ${fixed.map(([p]) => p.name).join(", ")} — outline is clean to cut`);
    let conn = { ...DEFAULT_CONNECTION, ...state.connection };
    if ((user?.offline_session || isOffline()) && bundleNow() && user?.role !== "admin") {
      // Offline mode: the server relay is unreachable, so ask Xpattern Software on THIS computer what is plugged in.
      const st = await localStatus();
      const sug = st?.devices?.suggested;
      if (!st || !sug) return set({ dialog: "noPlotter" });
      conn = { ...conn, transport: "bridge", targetType: sug.type, printerName: sug.printerName || "", serialPort: sug.serialPort || "", baud: sug.baud || conn.baud, pinned: true };
      localStorage.setItem("xpattern.connection", JSON.stringify(conn));
      set({ connection: conn, dialog: "cut", locked: true, selectedId: null, offsetPreview: 0 });
      return;
    }
    if (conn.transport === "download" && user?.role !== "admin") { conn = { ...conn, transport: "bridge" }; localStorage.setItem("xpattern.connection", JSON.stringify(conn)); set({ connection: conn }); } // cut files are owner-only — customers cut on the connected plotter
    if (conn.transport !== "download" && !plotterConnected(conn, state.bridge)) {
      // The connector sees a plotter but the saved target is stale/different - adopt the plotter that is actually plugged in.
      const live = cutterDevices(state.bridge), sug = state.bridge?.suggested;
      const sugName = sug?.type === "printer" ? rawQueueFor(state.bridge, sug.printerName) || sug.printerName : "";  // a suggested driver queue means its raw sibling
      const d = live.find((x) => x.type === sug?.type && (x.printerName === sugName || x.serialPort === sug?.serialPort)) || (live.length === 1 ? live[0] : null);
      if (!d) return set({ dialog: "noPlotter" });
      conn = { ...conn, transport: "bridge", targetType: d.type, printerName: d.printerName || "", serialPort: d.serialPort || "", baud: d.baud || conn.baud, pinned: true };
      localStorage.setItem("xpattern.connection", JSON.stringify(conn));
      set({ connection: conn });
    }
    set({ dialog: "cut", locked: true, selectedId: null, offsetPreview: 0 });
  };
  const fit = () => set({ fitNonce: state.fitNonce + 1 });
  const openDialog = (dialog) => set({ dialog });

  // Corner radius for every door / quarter window ("off" = exactly as DigiCut draws) — follows the ACCOUNT, so the same corners
  // come out on every computer the shop signs in from.
  const setAutoRound = (v) => {
    dispatch({ type: "SET_AUTO_ROUND", value: v });
    api.savePrefs({ corner_round: v, corner_round_frameless: state.framelessRound }).catch(() => {});
  };
  const setFramelessRound = (v) => {
    dispatch({ type: "SET_FRAMELESS_ROUND", value: v });
    api.savePrefs({ corner_round: state.autoRound, corner_round_frameless: v }).catch(() => {});
  };
  // Glass-match on frameless door glass: the glass corner radius + hairline margin, saved on the vehicle (owner) so it is right for every shop
  const setVehicleGlass = (glass) => {
    dispatch({ type: "SET_VEHICLE_GLASS", glass });
    if (state.vehicle?.id && user?.role === "admin") api.updateVehicleGlass(state.vehicle.id, glass).catch(() => toast.error("Could not save the glass setting on this vehicle"));
  };

  // Gasket switch: re-sizes every untouched roll-up pattern on the roll (library outline + auto-round ± the gasket inset).
  const setGasket = (v) => {
    set({ gasket: v });
    state.pieces.forEach((p) => {
      if (p.edited || p.appliedOffset || !p.rawPoints || p.decalId) return;
      const pts = roundedPoints(p, state.autoRound, v, state.framelessRound);
      dispatch({ type: "UPDATE_PIECE", id: p.id, patch: { points: pts, basePoints: pts, dirty: false } });
    });
  };

  // Top edge allowance: same re-size of every untouched roll-up pattern on the roll.
  const setTopExtend = (v) => {
    setTopExtendValue(v);
    set({ topExtend: v });
    resizeUntouched();
  };

  // Edge offset (±1/32″ steps, all around every window): the installer's flush-cut dial for their plotter / film.
  const setEdgeOffset = (v) => {
    setEdgeOffsetValue(v);
    set({ edgeOffset: v });
    resizeUntouched();
  };

  const resizeUntouched = () => {
    state.pieces.forEach((p) => {
      if (p.edited || p.appliedOffset || !p.rawPoints || p.decalId) return;
      const pts = roundedPoints(p, state.autoRound, state.gasket, state.framelessRound);
      dispatch({ type: "UPDATE_PIECE", id: p.id, patch: { points: pts, basePoints: pts, dirty: false } });
    });
  };

  // Manual eyebrow from a windshield (Owner now; customers once "Eyebrow tool" is switched live). Roll-only — never saved to the library.
  const eyebrowAllowed = user?.role === "admin" || Boolean(state.liveServices?.EyebrowTool);
  const canEyebrow = Boolean(eyebrowAllowed && sel && sel.included && sel.pieceType === "windshield" && !sel.eyebrowHalf && !locked);
  const startEyebrow = () => {
    if (!canEyebrow) return toast.error("Select a windshield on the roll first");
    set({ eyebrow: { depth: sel.eyebrow?.depth ?? EYEBROW_DEFAULT.depth, mode: sel.eyebrow?.mode ?? EYEBROW_DEFAULT.mode }, tool: "select", offsetPreview: 0 });
  };
  const cancelEyebrow = () => set({ eyebrow: null });
  const applyEyebrow = () => {
    const eb = state.eyebrow;
    if (!eb || !sel) return;
    const { src, parts } = eyebrowSource(sel, state.pieces);
    const r = eyebrowCut(src, eb.depth, eb.mode);
    if (!r) return toast.error("Could not cut an eyebrow at that height");
    if (parts.length > 1) {
      // split windshield → ONE visor: a new roll-only piece where the halves sat; the halves leave the roll and come back with "Full windshield"
      const id = `${sel.id}-eyebrow`, ids = new Set(parts.map((p) => p.id));
      const piece = { ...sel, id, name: sel.setName || sel.name.replace(/,\s*(Part \d+|Left|Right)$/i, ""), setKey: "", setName: "", setOffset: null, setRank: undefined, setRot: undefined, groupId: null, loose: false,
        points: r.points, basePoints: r.points, rawPoints: r.points, holes: [], measure: undefined, eyebrow: { depth: eb.depth, mode: eb.mode }, eyebrowSource: src,
        eyebrowJoined: parts.map((p) => ({ id: p.id, x: p.x, y: p.y, rotation: p.rotation })), edited: true, dirty: false, appliedOffset: 0, included: true };
      const pieces = state.pieces.flatMap((p) => (ids.has(p.id) ? [{ ...p, included: false, groupId: null }] : [p])).concat([piece]);
      dispatch({ type: "SET_PIECES", pieces, commit: true });
      set({ eyebrow: null, selectedId: id });
      return toast.success(`Eyebrow ${fmtIn(eb.depth)} tall — ${parts.length} halves joined into one piece`);
    }
    const holes = (sel.holes || []).filter((h) => h.every((p) => r.inside(p)));
    upd({ points: r.points, holes, eyebrow: { depth: eb.depth, mode: eb.mode }, eyebrowSource: src, edited: true, dirty: false, appliedOffset: 0 });
    set({ eyebrow: null });
    toast.success(`Eyebrow ${fmtIn(eb.depth)} tall — the rest of the windshield was removed`);
  };
  const removeEyebrow = () => {
    if (!sel?.eyebrow) return;
    const origId = sel.eyebrowHalf === "right" ? sel.eyebrowTwin : sel.id, twin = sel.eyebrowHalf ? (sel.eyebrowHalf === "right" ? sel.id : sel.eyebrowTwin) : null;
    const orig = state.pieces.find((p) => p.id === origId) || sel;
    let pieces, selectId = origId;
    if (orig.eyebrowJoined) {  // one-piece visor from a split windshield: the halves come back exactly where they were
      const back = new Map(orig.eyebrowJoined.map((s) => [s.id, s]));
      pieces = state.pieces.filter((p) => p.id !== origId && p.id !== twin).map((p) => (back.has(p.id) ? { ...p, ...back.get(p.id), included: true, groupId: null } : p));
      selectId = orig.eyebrowJoined[0].id;
    } else {
      pieces = state.pieces.filter((p) => p.id !== twin).map((p) => (p.id === origId ? { ...p, points: p.eyebrowSource || sel.eyebrowSource, holes: [], eyebrow: null, eyebrowSource: null, eyebrowHalf: null, eyebrowTwin: null } : p));
    }
    dispatch({ type: "SET_PIECES", pieces, commit: true });
    set({ selectedId: selectId });
    toast.success("Full windshield restored");
  };
  // an eyebrow in two halves (left / right) for narrow film or easier handling
  const splitEyebrow = () => {
    if (!sel?.eyebrow || sel.eyebrowHalf) return;
    const cx = bbox(sel.points).cx, L = clipX(sel.points, cx, true), R = clipX(sel.points, cx, false);
    if (L.length < 3 || R.length < 3) return toast.error("Could not split this eyebrow");
    const twinId = `${sel.id}-eb2`;
    const left = { ...sel, points: L, holes: [], eyebrowHalf: "left", eyebrowTwin: twinId, dirty: false };
    const right = { ...sel, id: twinId, points: R, holes: [], eyebrowHalf: "right", eyebrowTwin: sel.id, included: false, x: 0, y: 0, dirty: false };
    const pieces = [...state.pieces.map((p) => (p.id === sel.id ? left : p)), right];
    const r = addToRoll(pieces, [twinId], state, [sel.rotation], true);
    dispatch({ type: "SET_PIECES", pieces: r.overflow.length ? addToRoll(pieces, [twinId], state).pieces : r.pieces, commit: true });
    toast.success("Eyebrow split into a left and a right half");
  };

  // Trim knife: a straight cut through a decal on the roll — both sides become their own pieces (job-only, never saved).
  const canKnife = Boolean(sel && sel.included && sel.pieceType === "decal" && !locked && !sel.eyebrow);
  const startKnife = () => {
    if (!canKnife) return toast.error("Select a graphic on the roll first");
    set({ knife: { a: null, b: null }, eyebrow: null, tool: "select", offsetPreview: 0 });
  };
  const cancelKnife = () => set({ knife: null });
  const applyKnife = () => {
    const k = state.knife;
    if (!k?.a || !k?.b || !sel) return;
    const r = knifeCut(sel, k.a, k.b);
    if (!r) return toast.error("Drag the line all the way across the graphic");
    const group = sel.trimGroup || sel.id, src = sel.trimSource || { points: sel.points, holes: sel.holes || [] };
    const n = Math.max(1, sel.trimNo || 1, ...state.pieces.filter((p) => p.trimGroup === group).map((p) => p.trimNo || 1));
    const twinId = `${group}-t${n + 1}`;
    const A = { ...sel, ...r.L, trimGroup: group, trimSource: src, trimNo: sel.trimNo || 1, edited: true, dirty: false };
    const B = { ...sel, ...r.R, id: twinId, trimGroup: group, trimSource: src, trimNo: n + 1, trimOf: group, edited: true, dirty: false, included: false, x: 0, y: 0 };
    const pieces = [...state.pieces.map((p) => (p.id === sel.id ? A : p)), B];
    const strict = addToRoll(pieces, [twinId], state, [sel.rotation], true);
    const res = strict.overflow.length ? addToRoll(pieces, [twinId], state) : strict;
    if (res.overflow.length) return toast.error("No room on the roll for the second piece — widen the film or clear some pieces");
    dispatch({ type: "SET_PIECES", pieces: res.pieces, commit: true });
    set({ knife: null });
    toast.success("Graphic cut in two — drag the pieces apart or cut again");
  };
  const restoreTrim = () => {
    const group = sel?.trimGroup;
    if (!group) return;
    const orig = state.pieces.find((p) => p.id === group) || sel;
    const pieces = state.pieces.filter((p) => p.trimGroup !== group || p.id === orig.id).map((p) => (p.id === orig.id ? { ...p, points: orig.trimSource.points, holes: orig.trimSource.holes, trimGroup: null, trimSource: null, trimNo: null, trimOf: null, dirty: false } : p));
    dispatch({ type: "SET_PIECES", pieces, commit: true });
    set({ selectedId: orig.id, knife: null });
    toast.success("Whole graphic restored");
  };

  // PPF wrap (hotkey W): extend chosen edges of the selected pattern by a wrap depth — job-only, the library template stays as drawn.
  const canWrap = Boolean(sel && sel.included && !locked && !sel.eyebrow && !sel.multi);
  const startWrap = () => {
    if (!canWrap) return toast.error("Select a pattern on the roll first");
    set({ wrap: { depth: sel.wrap?.depth || WRAP_DEFAULT }, knife: null, eyebrow: null, tool: "select", offsetPreview: 0 });
  };
  const cancelWrap = () => set({ wrap: null });
  const wrapDepth = sel?.wrap?.depth ?? state.wrap?.depth ?? WRAP_DEFAULT;  // the piece's own depth wins (undo may have changed it)
  // keep a freshly wrapped piece inside the film and clear of its neighbours (the band grows outward in place)
  const settleWrapped = (pieces, id) => {
    const me = pieces.find((p) => p.id === id);
    if (!me?.included) return pieces;
    const others = pieces.filter((p) => p.included && p.id !== id).map((p) => offsetPolygon(worldPoints(p), state.spacing));
    const fits = (q) => { const w = worldPoints(q), b = bbox(w); return b.minX >= state.margin - 1e-6 && b.maxX <= state.rollWidth - state.margin + 1e-6 && b.minY >= state.margin - 1e-6 && !others.some((o) => polysIntersect(o, w)); };
    if (fits(me)) return pieces;
    const b = bbox(worldPoints(me));
    let q = { ...me, x: me.x + Math.max(0, state.margin - b.minX) - Math.max(0, b.maxX - (state.rollWidth - state.margin)), y: me.y + Math.max(0, state.margin - b.minY) };
    if (!fits(q)) {
      const step = 0.125;
      const dirs = [[0, 1], [0, -1], [1, 0], [-1, 0], [1, 1], [-1, 1], [1, -1], [-1, -1]];
      let found = null;
      for (let d = step; d <= 3 && !found; d += step) for (const [dx, dy] of dirs) { const c = { ...q, x: q.x + dx * d, y: q.y + dy * d }; if (fits(c)) { found = c; break; } }
      if (!found) { toast.warning("Wrapped film reaches a neighbour — drag the pattern clear or re-nest"); return pieces.map((p) => (p.id === id ? q : p)); }
      q = found;
    }
    return pieces.map((p) => (p.id === id ? q : p));
  };
  const applyWrapTo = (piece, segs, depth) => {
    const next = wrapPiece(piece, segs, depth);
    dispatch({ type: "SET_PIECES", pieces: settleWrapped(state.pieces.map((p) => (p.id === piece.id ? next : p)), piece.id), commit: true });
  };
  const setWrapDepth = (depth) => {
    set({ wrap: { depth } });
    if (sel?.wrap) applyWrapTo(sel, sel.wrap.segs, depth);
  };
  const toggleWrapEdge = (segs) => {
    if (!sel || !state.wrap) return;
    const cur = new Set(sel.wrap?.segs || []);
    const on = segs.every((i) => cur.has(i));
    segs.forEach((i) => (on ? cur.delete(i) : cur.add(i)));
    applyWrapTo(sel, [...cur], wrapDepth);
  };
  const invertWrap = () => {
    if (!sel || !state.wrap) return;
    const base = sel.wrapBase || sel.points, cur = new Set(sel.wrap?.segs || []);
    applyWrapTo(sel, base.map((_, i) => i).filter((i) => !cur.has(i)), wrapDepth);
  };
  const clearWrap = () => { if (sel?.wrap) applyWrapTo(sel, [], 0); };
  const wrapMate = sel ? state.pieces.find((p) => p.id !== sel.id && p.pieceType === sel.pieceType && p.side && sel.side && p.side !== sel.side && (p.setKey || null) === (sel.setKey || null)) : null;
  const mirrorWrap = () => {
    if (!sel?.wrap || !wrapMate) return;
    const segs = mirrorWrapSegs(sel.wrapBase, sel.wrap.segs, wrapMate.wrapBase || wrapMate.points);
    applyWrapTo(wrapMate, segs, sel.wrap.depth);
    toast.success(`Wrap mirrored to ${wrapMate.name}`);
  };
  // Saved wrap for this panel (shop-wide): re-applied automatically whenever the template is added to a roll.
  const wrapPreset = sel ? state.wrapPresets?.[sel.id] : null;
  const wrapRemembered = Boolean(wrapPreset && sel?.wrap && Math.abs(wrapPreset.depth - sel.wrap.depth) < 1e-6 && wrapPreset.segs.length === sel.wrap.segs.length && wrapPreset.segs.every((i) => sel.wrap.segs.includes(i)));
  const rememberWrap = async () => {
    if (!sel?.wrap) return;
    try {
      const r = await api.putWrapPreset(sel.id, { segs: sel.wrap.segs, depth: sel.wrap.depth });
      set({ wrapPresets: { ...state.wrapPresets, [sel.id]: { segs: r.segs, depth: r.depth } } });
      toast.success(`Wrap remembered for ${sel.name} — it will apply whenever this panel is added`);
    } catch (e) { toast.error(apiError(e, "Could not save the wrap")); }
  };
  const forgetWrap = async () => {
    if (!sel || !wrapPreset) return;
    try {
      await api.deleteWrapPreset(sel.id);
      const { [sel.id]: _gone, ...rest } = state.wrapPresets;
      set({ wrapPresets: rest });
      toast.success(`Saved wrap for ${sel.name} forgotten`);
    } catch (e) { toast.error(apiError(e, "Could not remove the saved wrap")); }
  };

  return { state, dispatch, user, sel, locked, included, saving, set, upd, rotate, mirror, nest, emptyRoll, removeSelected, deletePattern, saveAll, cut, fit, openDialog, setMates, setSplit, splitSet, joinSet,
    setGasket, setTopExtend, setEdgeOffset, setAutoRound, setFramelessRound, setVehicleGlass, eyebrowAllowed, canEyebrow, startEyebrow, cancelEyebrow, applyEyebrow, removeEyebrow, splitEyebrow, canKnife, startKnife, cancelKnife, applyKnife, restoreTrim,
    canWrap, startWrap, cancelWrap, wrapDepth, setWrapDepth, toggleWrapEdge, invertWrap, clearWrap, wrapMate, mirrorWrap, wrapPreset, wrapRemembered, rememberWrap, forgetWrap,
    shadowPieces, toggleShadowPiece, setShadowGap };
};
